<script setup lang="ts">
import type { VueMessageType } from 'vue-i18n'
import { match } from 'ts-pattern'

interface VI18nProps {
  keyPath: string
  tag?: string
  plural?: number | string
}

const props = withDefaults(defineProps<VI18nProps>(), {
  tag: 'p',
  plural: 0
})

const { te, baseTm, computeKey, isVueI18nMessageAst } = useI18n()

const computedKey = computed(() => computeKey(props.keyPath))

// Node type numbers match `@intlify/message-compiler`'s `NodeTypes` const enum
// (Resource, Plural, Message, Text, Named); only `Named` nodes correspond to a
// `{slotKey}` interpolation, which is all `VI18n.vue` needs to find slot names.
const collectNamedMessageKeys = (node: unknown, keys: Set<string>) => {
  if (!node || typeof node !== 'object') {
    return keys
  }

  const { type } = node as { type: number }

  match(type)
    .with(0, () => collectNamedMessageKeys((node as { body: unknown }).body, keys))
    .with(1, () =>
      (node as { cases: unknown[] }).cases?.forEach((item) =>
        collectNamedMessageKeys(item, keys)
      )
    )
    .with(2, () =>
      (node as { items: unknown[] }).items?.forEach((item) =>
        collectNamedMessageKeys(item, keys)
      )
    )
    .with(4, () => keys.add((node as { key: string }).key))
    .otherwise(() => {})

  return keys
}

const placeholderRegex = /({\S+})/g
const extractPlaceholderKeys = (message: VueMessageType | undefined): string[] => {
  if (!message) {
    return []
  }

  // If message is a string, run the easy way of getting the slots
  if (typeof message === 'string') {
    return message
      .split(' ')
      .filter((word) => word.match(placeholderRegex))
      .map((word) => word.substring(word.indexOf('{') + 1, word.lastIndexOf('}')))
  }

  // Else, check if the object is a VueI18n message AST and collect the keys
  if (!isVueI18nMessageAst(message)) {
    return []
  }

  const keys = collectNamedMessageKeys(message, new Set<string>())
  return [...keys]
}

const keys = computed(() => {
  if (!te(computedKey.value)) {
    return []
  }

  return extractPlaceholderKeys(baseTm(computedKey.value))
})
</script>

<template>
  <i18n-t scope="global" :keypath="computedKey" :tag="tag" :plural="plural">
    <template v-for="slotKey in Object.keys($slots)" :key="slotKey" #[slotKey]>
      <slot v-if="keys.includes(slotKey)" :name="slotKey" />
    </template>
  </i18n-t>
</template>

<style scoped></style>
